/**
 * Edit a Binom landing — Name + URL + Group + Language. Save writes via
 * PUT /binom/landings/:id → /landing/modify/:id. Edits the GLOBAL lander
 * entity (warning banner shown).
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useEffect, useState } from 'react';

import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/layout/Drawer';
import { Button } from '@/components/ui/layout/Button';
import { LoadingSpinner } from '@/components/ui/feedback/LoadingSpinner';
import {
  useBinomGroups,
  useBinomLanding,
  useBinomLanguages,
  useUpdateBinomLanding,
  type BinomLandingDetail,
} from '@/hooks/binom';

import BinomLanderEditBody from './body/BinomLanderEditBody';

export interface BinomLanderEditDialogProps {
  landerId: string;
  onClose: () => void;
}

const BinomLanderEditDialog: FC<BinomLanderEditDialogProps> = ({
  landerId,
  onClose,
}) => {
  const landingQuery = useBinomLanding(landerId);
  const update = useUpdateBinomLanding(landerId);
  const groupsQuery = useBinomGroups('landings');
  const languagesQuery = useBinomLanguages();
  const [form, setForm] = useState<BinomLandingDetail | null>(null);

  useEffect(() => {
    if (landingQuery.data && !form) setForm(landingQuery.data);
  }, [landingQuery.data, form]);

  const groupOptions = (Array.isArray(groupsQuery.data) ? groupsQuery.data : [])
    .map((g) => ({ value: g.uuid, label: g.name }));
  const languageOptions = (Array.isArray(languagesQuery.data) ? languagesQuery.data : [])
    .map((l) => ({ value: l.code, label: `${l.name} (${l.code})` }));

  const save = async () => {
    if (!form) return;
    await update.mutateAsync(form);
    onClose();
  };

  return (
    <Drawer open onOpenChange={(o) => !o && onClose()}>
      <DrawerContent variant="center" width="w-[40rem]">
        <DrawerHeader>
          <DrawerTitle>
            Edit landing: {form?.name ?? landingQuery.data?.name ?? landerId}
          </DrawerTitle>
        </DrawerHeader>
        <DrawerBody>
          {landingQuery.isLoading || !form ? (
            <div className="flex justify-center py-6">
              <LoadingSpinner />
            </div>
          ) : (
            <BinomLanderEditBody
              form={form}
              onChange={setForm}
              groupOptions={groupOptions}
              languageOptions={languageOptions}
            />
          )}
        </DrawerBody>
        <DrawerFooter>
          <Button type="button" variant="outline" onClick={onClose} size="sm">
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!form || update.isPending}
            loading={update.isPending}
            onClick={save}
          >
            Save
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default BinomLanderEditDialog;
